import type { ComponentPropsWithRef } from 'react';
import { Icon } from '../icons/Icon';
import type { IconName } from '../icons/registry';
import { cn } from '../lib/cn';
import { disabledStyles, focusRing, focusRingOnDark } from '../lib/styles';

export type IconButtonVariant = 'outline' | 'onDark' | 'primary' | 'dark' | 'ghost';

/** Square control. 40 · 44 (the opname/public minimum) · 46. */
export type IconButtonSize = 'sm' | 'md' | 'lg';

const variantStyles: Record<IconButtonVariant, string> = {
    outline:
        'border border-[var(--border-control)] bg-[var(--surface-card)] text-[var(--text-primary)] hover:border-[var(--accent)] hover:text-[var(--accent)]',
    onDark: 'border border-[var(--ink-500)] bg-transparent text-[var(--text-on-dark-2)] hover:bg-[var(--ink-hover-on-dark)]',
    primary:
        'border-0 bg-[var(--accent)] text-[var(--accent-text)] hover:bg-[var(--accent-hover)]',
    dark: 'border-0 bg-[var(--ink-800)] text-[var(--text-on-dark)] hover:bg-[var(--ink-600)]',
    ghost: 'border-0 bg-transparent text-[var(--text-secondary)] hover:bg-[var(--surface-row-hover)] hover:text-[var(--text-primary)]',
};

const sizeStyles: Record<IconButtonSize, string> = {
    sm: 'h-10 w-10 rounded-[7px]',
    md: 'h-11 w-11 rounded-[7px]',
    lg: 'h-[46px] w-[46px] rounded-[8px]',
};

export type IconButtonProps = Omit<ComponentPropsWithRef<'button'>, 'children' | 'aria-label'> & {
    variant?: IconButtonVariant;
    size?: IconButtonSize;
    /** The line icon, by meaning (`close`, `edit`, `delete`). Drawn at 18px. */
    icon: IconName;
    /** Required: an icon-only control must announce itself. */
    'aria-label': string;
};

export function IconButton({
    variant = 'outline',
    size = 'md',
    icon,
    className,
    type = 'button',
    ...rest
}: IconButtonProps) {
    return (
        <button
            type={type}
            className={cn(
                'inline-flex shrink-0 items-center justify-center',
                'font-[family-name:var(--font-body)] cursor-pointer transition-colors duration-100',
                sizeStyles[size],
                variantStyles[variant],
                variant === 'onDark' ? focusRingOnDark : focusRing,
                disabledStyles,
                className,
            )}
            {...rest}
        >
            <Icon name={icon} size="md" />
        </button>
    );
}
